import React from "react";
import { Text, View } from "react-native";
import {
  InputPatientDetail,
  InputPatientDetailDropdown,
  NormalText,
  SecondaryButton,
} from "../../../common/native";
import { TAppearance } from "./AppearanceTab";
import CopyableCodeBlock from "./CopyableCodeBlock";
import type { DeployDocSection } from "../../../../docs/deployDocs";
import { getLeadCaptureFormBaseUrl } from "../../../../utils/leadCaptureLinks";
import { DEPLOY_WIDGET_TOOLTIPS } from "./deployWidgetTooltips";

export type TDeployFormName =
  | "onboarding-tier1"
  | "onboarding-scan"
  | "onboarding-scan-all-poses"
  | "onboarding-advertise"
  | "onboarding-chatbot"
  | "onboarding-tier1-chatbot"
  | "onboarding-single-procedure"
  | "onboarding-lite";

export type TFormFormatConfig = {
  formName: TDeployFormName;
  lang: "en" | "es" | "tr" | "pt" | "fr" | "th" | "ar" | "ko" | "de";
  procedure: string;
  configUrl: string;
  name: string;
  email: string;
  phone: string;
  referrer: string;
  formVersion: string;
  isMobile: "true" | "false";
  ctaCopy: string;
  collectEmail: "true" | "false";
  collectSmsConsent: "true" | "false";
};

type FormFormatTabProps = {
  clinicSdkKey?: string;
  appearance: TAppearance;
  formFormat: TFormFormatConfig;
  shouldRenderPreview: boolean;
  codeSnippet: string;
  deployMode: "widget" | "iframe";
  deployStatus?: boolean;
  showAllFormTypes?: boolean;
  onUpdateFormFormat: (field: keyof TFormFormatConfig, value: string) => void;
  onOpenHelpSection: (sectionId: DeployDocSection["id"]) => void;
  onBack: () => void;
};

const FORM_OPTIONS: { value: TDeployFormName; label: string }[] = [
  { value: "onboarding-tier1", label: "Full Form (Recommended)" },
  { value: "onboarding-scan", label: "Onboarding Scan" },
  { value: "onboarding-scan-all-poses", label: "Onboarding Scan (All Poses)" },
  { value: "onboarding-advertise", label: "Onboarding Advertise" },
  { value: "onboarding-chatbot", label: "Onboarding Chatbot" },
  { value: "onboarding-tier1-chatbot", label: "Onboarding Tier1 Chatbot" },
  {
    value: "onboarding-single-procedure",
    label: "Onboarding Single Procedure",
  },
  { value: "onboarding-lite", label: "Embedded iframe Form" },
];

const PROCEDURE_OPTIONS = [
  "BBL",
  "BREAST_AUGMENTATION",
  "LIP_FILLER",
  "RHINOPLASTY",
  "TUMMY_TUCK",
];

const LANGUAGE_OPTIONS: { value: TFormFormatConfig["lang"]; label: string }[] =
  [
    { value: "en", label: "English" },
    { value: "es", label: "Spanish" },
    { value: "tr", label: "Turkish" },
    { value: "pt", label: "Portuguese" },
    { value: "fr", label: "French" },
    { value: "th", label: "Thai" },
    { value: "ar", label: "Arabic" },
    { value: "ko", label: "Korean" },
    { value: "de", label: "German" },
  ];

const FORM_PREVIEW_MAX_WIDTH = 350;

const DEPLOY_WIDGET_TOOLTIP_EVENT = "Deploy Widget Tooltip Opened";

const YES_NO_OPTIONS = ["Yes", "No"];

const toYesNo = (value: "true" | "false") => (value === "false" ? "No" : "Yes");

const fromYesNo = (label: string) => (label === "No" ? "false" : "true");

const CMS_HELP_OPTIONS: {
  label: string;
  sectionId: DeployDocSection["id"];
}[] = [
  { label: "WordPress", sectionId: "wordpress" },
  { label: "Webflow", sectionId: "webflow" },
  { label: "Wix", sectionId: "wix" },
  { label: "Squarespace", sectionId: "squarespace" },
  { label: "Shopify", sectionId: "shopify" },
  { label: "Custom HTML", sectionId: "custom-html" },
];

const appendIfPresent = (
  params: URLSearchParams,
  key: string,
  value: string,
) => {
  if (value?.trim()) params.set(key, value.trim());
};

export const buildFormPreviewUrl = (
  clinicSdkKey: string,
  appearance: TAppearance,
  formFormat: TFormFormatConfig,
): string => {
  const baseUrl = getLeadCaptureFormBaseUrl();
  const params = new URLSearchParams();

  params.set("clinicSdkKey", clinicSdkKey);
  params.set("themeColor", appearance.themeColor);
  params.set("lang", formFormat.lang);
  params.set("fontFamily", appearance.fontFamily);

  if (formFormat.formName === "onboarding-tier1") {
    appendIfPresent(params, "referrer", formFormat.referrer);
    appendIfPresent(params, "formVersion", formFormat.formVersion);
    appendIfPresent(params, "isMobile", formFormat.isMobile);
    appendIfPresent(params, "ctaCopy", formFormat.ctaCopy);
  }

  if (formFormat.formName === "onboarding-scan") {
    params.set("procedure", formFormat.procedure || "BBL");
    appendIfPresent(params, "name", formFormat.name);
    appendIfPresent(params, "email", formFormat.email);
    appendIfPresent(params, "phone", formFormat.phone);
  }

  if (formFormat.formName === "onboarding-scan-all-poses") {
    params.set("procedure", formFormat.procedure || "BBL");
  }

  if (formFormat.formName === "onboarding-advertise") {
    params.set("procedure", formFormat.procedure || "BBL");
  }

  if (formFormat.formName === "onboarding-tier1-chatbot") {
    appendIfPresent(params, "referrer", formFormat.referrer);
  }

  if (formFormat.formName === "onboarding-single-procedure") {
    appendIfPresent(params, "configUrl", formFormat.configUrl);
    appendIfPresent(params, "referrer", formFormat.referrer);
    appendIfPresent(params, "formVersion", formFormat.formVersion);
    appendIfPresent(params, "isMobile", formFormat.isMobile);
    appendIfPresent(params, "ctaCopy", formFormat.ctaCopy);
  }

  if (formFormat.formName === "onboarding-lite") {
    appendIfPresent(params, "referrer", formFormat.referrer);
    appendIfPresent(params, "formVersion", formFormat.formVersion);
    appendIfPresent(params, "isMobile", formFormat.isMobile);
    appendIfPresent(params, "ctaCopy", formFormat.ctaCopy);
    // The form collects both by default, so only an opt-out needs a param.
    if (formFormat.collectEmail === "false") {
      params.set("collectEmail", "false");
    }
    if (formFormat.collectSmsConsent === "false") {
      params.set("collectSmsConsent", "false");
    }
  }

  return `${baseUrl}/${formFormat.formName}?${params.toString()}`;
};

const FormFormatTab: React.FC<FormFormatTabProps> = ({
  clinicSdkKey,
  appearance,
  formFormat,
  shouldRenderPreview,
  codeSnippet,
  deployMode,
  deployStatus,
  showAllFormTypes = false,
  onUpdateFormFormat,
  onOpenHelpSection,
  onBack,
}) => {
  const canPreview = Boolean(clinicSdkKey);
  const previewUrl = React.useMemo(
    () =>
      canPreview
        ? buildFormPreviewUrl(clinicSdkKey as string, appearance, formFormat)
        : "",
    [canPreview, clinicSdkKey, appearance, formFormat],
  );

  const supportsProcedure = [
    "onboarding-scan",
    "onboarding-scan-all-poses",
    "onboarding-advertise",
  ].includes(formFormat.formName);

  const supportsContactPrefill = formFormat.formName === "onboarding-scan";
  const supportsConfigUrl =
    formFormat.formName === "onboarding-single-procedure";
  const supportsTracking = [
    "onboarding-tier1",
    "onboarding-single-procedure",
    "onboarding-lite",
  ].includes(formFormat.formName);
  const supportsReferrerOnly =
    formFormat.formName === "onboarding-tier1-chatbot";
  const supportsLeadFields = formFormat.formName === "onboarding-lite";

  const visibleFormOptions = React.useMemo(() => {
    if (showAllFormTypes) return FORM_OPTIONS;
    return FORM_OPTIONS.filter((option) =>
      ["onboarding-tier1", "onboarding-lite"].includes(option.value),
    );
  }, [showAllFormTypes]);

  const labelByValue = React.useMemo(
    () =>
      Object.fromEntries(
        FORM_OPTIONS.map((option) => [option.value, option.label]),
      ) as Record<TDeployFormName, string>,
    [],
  );

  const valueByLabel = React.useMemo(
    () =>
      Object.fromEntries(
        FORM_OPTIONS.map((option) => [option.label, option.value]),
      ) as Record<string, TDeployFormName>,
    [],
  );

  const languageLabelByValue = React.useMemo(
    () =>
      Object.fromEntries(
        LANGUAGE_OPTIONS.map((option) => [option.value, option.label]),
      ) as Record<TFormFormatConfig["lang"], string>,
    [],
  );

  const languageValueByLabel = React.useMemo(
    () =>
      Object.fromEntries(
        LANGUAGE_OPTIONS.map((option) => [option.label, option.value]),
      ) as Record<string, TFormFormatConfig["lang"]>,
    [],
  );
  const cmsSectionByLabel = React.useMemo(
    () =>
      Object.fromEntries(
        CMS_HELP_OPTIONS.map((option) => [option.label, option.sectionId]),
      ) as Record<string, DeployDocSection["id"]>,
    [],
  );

  return (
    <View style={{ flex: 1, gap: 10, marginTop: 10 }}>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
        <View
          style={{ flex: 1, width: "100%", minWidth: 350, gap: 10, zIndex: 1 }}
        >
          <NormalText text="Pick Form Format" fontWeight="600" />
          <NormalText text="Choose the form type, adjust form-specific options, and preview before deployment." />

          <View style={{ width: "100%", maxWidth: 440, gap: 10 }}>
            <View style={{ zIndex: 9 }}>
              <InputPatientDetailDropdown
                name="Form Type"
                value={[labelByValue[formFormat.formName]]}
                selections={visibleFormOptions.map((option) => option.label)}
                onSelect={(value) =>
                  onUpdateFormFormat("formName", valueByLabel[value[0]])
                }
              />
            </View>
            <Text
              style={{
                fontFamily: "Urbanist500",
                fontSize: 14,
                color: "#868B97",
              }}
            >
              {
                visibleFormOptions.find(
                  (option) => option.value === formFormat.formName,
                )?.label
              }
            </Text>
            <View style={{ zIndex: 8 }}>
              <InputPatientDetailDropdown
                name="Language"
                value={[languageLabelByValue[formFormat.lang]]}
                selections={LANGUAGE_OPTIONS.map((option) => option.label)}
                onSelect={(value) =>
                  onUpdateFormFormat("lang", languageValueByLabel[value[0]])
                }
              />
            </View>

            {supportsProcedure ? (
              <View style={{ zIndex: 7 }}>
                <InputPatientDetailDropdown
                  name="Procedure (required)"
                  value={[formFormat.procedure]}
                  selections={PROCEDURE_OPTIONS}
                  onSelect={(value) =>
                    onUpdateFormFormat("procedure", value[0])
                  }
                />
              </View>
            ) : null}

            {supportsConfigUrl ? (
              <InputPatientDetail
                name="Config URL"
                value={formFormat.configUrl}
                onChangeText={(value) => onUpdateFormFormat("configUrl", value)}
              />
            ) : null}

            {supportsContactPrefill ? (
              <>
                <InputPatientDetail
                  name="Prefill Name"
                  value={formFormat.name}
                  onChangeText={(value) => onUpdateFormFormat("name", value)}
                />
                <InputPatientDetail
                  name="Prefill Email"
                  value={formFormat.email}
                  onChangeText={(value) => onUpdateFormFormat("email", value)}
                />
                <InputPatientDetail
                  name="Prefill Phone"
                  value={formFormat.phone}
                  onChangeText={(value) => onUpdateFormFormat("phone", value)}
                />
              </>
            ) : null}

            {supportsLeadFields ? (
              <>
                <View style={{ zIndex: 7 }}>
                  <InputPatientDetailDropdown
                    name="Collect Email"
                    textWidth={200}
                    value={[toYesNo(formFormat.collectEmail)]}
                    selections={YES_NO_OPTIONS}
                    tooltipText={DEPLOY_WIDGET_TOOLTIPS.formFormat.collectEmail}
                    tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                    tooltipTrackingProperties={{ Tab: "Deploy" }}
                    onSelect={(value) =>
                      onUpdateFormFormat("collectEmail", fromYesNo(value[0]))
                    }
                  />
                </View>
                <View style={{ zIndex: 6 }}>
                  <InputPatientDetailDropdown
                    name="SMS Consent Checkbox"
                    textWidth={200}
                    value={[toYesNo(formFormat.collectSmsConsent)]}
                    selections={YES_NO_OPTIONS}
                    tooltipText={
                      DEPLOY_WIDGET_TOOLTIPS.formFormat.collectSmsConsent
                    }
                    tooltipTrackingEventName={DEPLOY_WIDGET_TOOLTIP_EVENT}
                    tooltipTrackingProperties={{ Tab: "Deploy" }}
                    onSelect={(value) =>
                      onUpdateFormFormat(
                        "collectSmsConsent",
                        fromYesNo(value[0]),
                      )
                    }
                  />
                </View>
              </>
            ) : null}

            {/* Hidden per product request: these tracking/override controls are not needed in the UI right now. */}
            {/* {supportsTracking || supportsReferrerOnly ? (
              <InputPatientDetail
                name="Referrer"
                value={formFormat.referrer}
                onChangeText={(value) => onUpdateFormFormat("referrer", value)}
              />
            ) : null} */}

            {/* Hidden per product request: these tracking/override controls are not needed in the UI right now. */}
            {/* {supportsTracking ? (
              <>
                <InputPatientDetail
                  name="Form Version"
                  value={formFormat.formVersion}
                  onChangeText={(value) =>
                    onUpdateFormFormat("formVersion", value)
                  }
                />
                <View style={{ zIndex: 6 }}>
                  <InputPatientDetailDropdown
                    name="isMobile"
                    value={[formFormat.isMobile]}
                    selections={["true", "false"]}
                    onSelect={(value) =>
                      onUpdateFormFormat("isMobile", value[0])
                    }
                  />
                </View>
                <InputPatientDetail
                  name="CTA Copy"
                  value={formFormat.ctaCopy}
                  onChangeText={(value) => onUpdateFormFormat("ctaCopy", value)}
                />
              </>
            ) : null} */}

            <View style={{ marginTop: 12, gap: 10 }}>
              <NormalText
                text={"Deploy the form to your site"}
                fontWeight="600"
              />
              <Text
                style={{
                  fontFamily: "Urbanist500",
                  fontSize: 15,
                  color: "#0B0E1E",
                }}
              >
                {deployMode === "iframe" ? (
                  "Add the following iframe to the specific page where you want this form to appear.\nThis embeds only the form in-page, without the floating widget experience."
                ) : (
                  <>
                    Add the following code snippet to the{" "}
                    <Text
                      style={{
                        fontFamily: "Urbanist500",
                        backgroundColor: "rgba(0, 0, 0, 0.06)",
                        paddingHorizontal: 4,
                        paddingVertical: 1,
                        borderRadius: 4,
                      }}
                    >
                      {"<body>"}
                    </Text>{" "}
                    tag on your website
                  </>
                )}
              </Text>
              <View style={{ width: "100%", maxWidth: 440 }}>
                <CopyableCodeBlock code={codeSnippet} />
              </View>
              <View style={{ flexDirection: "row" }}>
                <NormalText
                  text={"Deploy Status: "}
                  fontWeight="600"
                  style={{ fontSize: 16 }}
                />
                <NormalText
                  text={deployStatus ? "Deployed!" : "Not Deployed"}
                  fontWeight="600"
                  style={{
                    color: deployStatus ? "#00B7F4" : "#868B97",
                    fontSize: 16,
                  }}
                />
              </View>
              <View style={{ width: "100%", maxWidth: 440, gap: 8, zIndex: 5 }}>
                <NormalText
                  text="Get detailed instructions for your site:"
                  fontWeight="600"
                  style={{ marginTop: 6 }}
                />
                <Text
                  style={{
                    fontFamily: "Urbanist500",
                    fontSize: 15,
                    color: "#0B0E1E",
                  }}
                >
                  What type of website do you have?
                </Text>
                <View style={{ zIndex: 5 }}>
                  <InputPatientDetailDropdown
                    name=""
                    placeholder="Choose website platform"
                    value={[]}
                    selections={CMS_HELP_OPTIONS.map((option) => option.label)}
                    onSelect={(value) => {
                      const selectedLabel = value[0];
                      if (!selectedLabel) return;
                      const sectionId = cmsSectionByLabel[selectedLabel];
                      if (!sectionId) return;
                      onOpenHelpSection(sectionId);
                    }}
                  />
                </View>
              </View>
            </View>
          </View>
        </View>
        <View
          style={{
            flex: 1,
            width: "100%",
            minWidth: 350,
            gap: 10,
          }}
        >
          <NormalText text={"Form Preview"} fontWeight="600" />
          <View
            style={{
              position: "relative",
              aspectRatio: 3 / 5,
              width: "100%",
              maxWidth: FORM_PREVIEW_MAX_WIDTH,
              borderRadius: 10,
              overflow: "hidden",
              marginTop: 10,
              borderWidth: 1,
              borderColor: "#DDDEE0",
              backgroundColor: "#F8F8F8",
            }}
          >
            {shouldRenderPreview && canPreview && previewUrl ? (
              <iframe
                src={previewUrl}
                allow="autoplay"
                allowFullScreen
                sandbox="allow-forms allow-same-origin allow-scripts"
                style={{
                  position: "absolute",
                  border: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "auto",
                }}
                scrolling="yes"
              >
                Browser does not support iframe
              </iframe>
            ) : (
              <View
                style={{
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 20,
                }}
              >
                <Text style={{ fontFamily: "Urbanist500", color: "#868B97" }}>
                  {canPreview
                    ? "Open this tab to load preview."
                    : "Add a clinic SDK key to load preview."}
                </Text>
              </View>
            )}
          </View>
          {previewUrl ? (
            <Text
              numberOfLines={2}
              style={{
                fontFamily: "Urbanist500",
                fontSize: 12,
                color: "#868B97",
              }}
            >
              {previewUrl}
            </Text>
          ) : null}
        </View>
      </View>

      <View
        style={{
          marginTop: "auto",
          justifyContent: "center",
          alignContent: "center",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <SecondaryButton
          text={"Back: Appearance"}
          roundBorderRadius
          width={275}
          onPress={onBack}
        />
      </View>
    </View>
  );
};

export default FormFormatTab;
